import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import WhatsAppLink from "@/components/admin/WhatsAppLink";

const KINDS = { copy: "Copy", right_click: "Right-click / long-press", print: "Print", save: "Save page", source: "View source", screenshot: "Screenshot key", drag: "Drag picture" };
const PERIODS = [7, 30, 90];
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Events({ userId }) {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get(`/admin/copy-attempts/${userId}`).then(({ data }) => setEvents(data.events)).catch(() => setEvents([])); }, [userId]);
  if (!events) return <Loader2 className="animate-spin text-yellow-300 m-3" size={16} />;
  return (
    <div className="mt-3 space-y-1 text-xs" data-testid={`copy-events-${userId}`}>
      {events.map((e, i) => (
        <div key={i} className="flex flex-wrap gap-3 text-slate-300" data-testid={`copy-event-${i}`}>
          <span className="text-slate-400 w-28 shrink-0">{when(e.at)}</span>
          <span className="font-semibold text-amber-200 w-44 shrink-0">{KINDS[e.kind] || e.kind}</span>
          <span>{e.child || "—"}</span>
          <span className="text-slate-500 truncate">{e.path}</span>
        </div>
      ))}
    </div>
  );
}

function PersonRow({ p, rank }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-4" data-testid={`copy-person-${p.user_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-yellow-300" data-testid={`copy-toggle-${p.user_id}`}>
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <span className="text-slate-500 font-bold w-6">#{rank}</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-100 truncate" data-testid="copy-person-email">{p.name ? `${p.name} · ` : ""}{p.email}</p>
          <p className="text-xs text-slate-400">{p.children.length ? `Children: ${p.children.join(", ")} · ` : ""}last attempt {when(p.last_at)}</p>
        </div>
        <span className="px-3 py-1 rounded-full bg-red-900/60 text-red-200 text-sm font-black" data-testid="copy-person-total">{p.total}</span>
        {p.whatsapp && <WhatsAppLink number={p.whatsapp} name={p.name} testId={`copy-wa-${p.user_id}`} />}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2 ml-14">
        {Object.entries(p.kinds).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
          <span key={k} className="px-2 py-0.5 rounded-full bg-slate-700 text-xs text-slate-200" data-testid={`copy-kind-${k}`}>{KINDS[k] || k}: {n}</span>
        ))}
      </div>
      {open && <div className="ml-14"><Events userId={p.user_id} /></div>}
    </div>
  );
}

export default function CopyAttempts() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/admin/copy-attempts", { params: { days } }).then(({ data }) => setData(data)).catch(() => setData({ people: [], total: 0 })); }, [days]);
  return (
    <div data-testid="admin-copy-attempts">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <ShieldAlert className="text-yellow-300" size={18} />
        <p className="text-sm text-slate-300 mr-3">Accounts that tried to copy, print or screenshot content (blocked).</p>
        {PERIODS.map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`copy-period-${d}`}
            className={`px-3 py-1 rounded-full border-2 text-xs font-semibold ${days === d ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600 text-slate-300"}`}>
            Last {d} days
          </button>
        ))}
        {data && <span className="text-xs text-slate-400 ml-auto" data-testid="copy-total">{data.total} attempt(s) · {data.people.length} account(s)</span>}
      </div>
      {!data && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-yellow-300" /></div>}
      {data && data.people.length === 0 && <p className="text-slate-400 text-center py-8" data-testid="copy-empty">No copy attempts in this period.</p>}
      <div className="space-y-3">
        {data?.people.map((p, i) => <PersonRow key={p.user_id} p={p} rank={i + 1} />)}
      </div>
    </div>
  );
}
